import Button from '@ingka/button';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './LotteryForm.module.scss';
import type { ModuleName } from './modules';
import { getModuleForm } from './modules';

export type FormPageProps = {
  templateId: string;
  retailUnit: string;
  modules: Array<ModuleName>;
  values: Record<string, unknown>;
  initialValues?: Record<string, unknown>;
  onFormSubmit: (formData: Record<string, unknown>) => void;
  submitTranslationKey: string;
  onSecondaryAction: (formData: Record<string, unknown>) => void;
  secondaryActionTranslationKey: string;
  wrapFormTitle?: (title: string) => React.ReactNode;
  getFormState: () => Record<string, unknown>;
};

export const FormPage: React.FC<FormPageProps> = ({
  templateId,
  retailUnit,
  modules,
  values,
  initialValues,
  submitTranslationKey,
  onFormSubmit,
  secondaryActionTranslationKey,
  onSecondaryAction,
  wrapFormTitle = (title: string) => title,
  getFormState,
}) => {
  const { t } = useTranslation();

  // To support "edit"-form, load initial values if they exist and no values has been set yet
  const refState = useRef<Record<string, unknown>>(
    Object.keys(values).length === 0 && initialValues
      ? Object.fromEntries(modules.map((name) => [name, initialValues[name]]))
      : values
  );
  const state = refState.current;

  // The validation for a particular module is stored here
  // Eg: validation[moduleName] // true or false
  const [validation, setValidation] = useState<Record<string, boolean>>({});

  const isValid = !Object.values(validation).some((isValid) => !isValid);

  const onChange = useCallback(
    (moduleName: string, formData: unknown) => {
      state[moduleName] = formData;
    },
    [state]
  );

  const onValidation = useCallback(
    (moduleName: string, isValid: boolean) => {
      setValidation((oldState) => {
        if (oldState?.[moduleName] === isValid) {
          // If no change, return old state to prevent returning a new object
          return oldState;
        }
        return { ...oldState, [moduleName]: isValid };
      });
    },
    [setValidation]
  );

  return (
    <>
      {wrapFormTitle(t('form-contest-configurations'))}
      <div className={styles['container']}>
        {modules.map((moduleName) => {
          const Component = getModuleForm(moduleName);
          return (
            <Component
              key={moduleName}
              templateId={templateId}
              retailUnit={retailUnit}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              values={state[moduleName] as any}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              initialValues={initialValues?.[moduleName] as any}
              getFormState={getFormState}
              onChange={onChange}
              onValidation={onValidation}
            />
          );
        })}
        <div className={styles['button-container']}>
          <Button
            text={`${t(submitTranslationKey)}`}
            type="primary"
            disabled={!isValid}
            onClick={() => onFormSubmit(state)}
            data-testid="button-submit"
          />
          <Button
            text={`${t(secondaryActionTranslationKey)}`}
            onClick={() => onSecondaryAction(state)}
          />
        </div>
      </div>
    </>
  );
};
